import { compact, isNumber } from "lodash";
import moment from "moment";
import { useCallback, useMemo } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  ComplianceProgramStep,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import Callout from "@components/callout/callout";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import DeprecatedSelect, { Select } from "@components/select/select";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import {
  assessmentGroupAnonymityOptions,
  assessmentGroupProviderOptions,
} from "@helpers/constants";
import { assertNonNull } from "@helpers/helpers";

import { getDefaultProgramFormStep } from "../form-data";
import { getMaxStepDueDateOffset, getMinStepDueDateOffset } from "../helpers";
import ProgramStepDueDate from "../steps/program-step-due-date";
import OneOnOneToggleCard from "../toggle-card/one-on-one-toggle-card";
import ToggleCard from "../toggle-card/toggle-card";
import {
  ComplianceProgramType,
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramFormStep,
  ProgramStartTrigger,
} from "../types";

const ProgramFormWorkflow = ({
  complianceProgramType,
  proposedComplianceProgram,
  canUpdate,
  onAddOrUpdateStep,
  onRemoveStep,
  onUpdateProposedComplianceProgram,
  errors,
}: {
  complianceProgramType: ComplianceProgramType;
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  onRemoveStep: (stepType: ComplianceProgramStep) => void;
  onUpdateProposedComplianceProgram: (data: Partial<ProgramForm>) => void;
  errors: FormError[];
}) => {
  const label = useLabel();
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const errorForField = (field: string) =>
    errors.find((error) => error.field === field)?.message;
  const errorForAnyField = (fields: string[]) =>
    errors.find((error) => fields.includes(error.field))?.message;

  const isPreCalibration =
    proposedComplianceProgram.calibration ===
    ComplianceProgramCalibrationType.PreCalibration;
  // The same form edits a published review, where "after you publish" is wrong.
  const isDraft =
    proposedComplianceProgram.state === ComplianceProgramState.Draft;

  const upwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.UpwardReview,
    );
  }, [proposedComplianceProgram]);
  const peerReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.PeerReview,
    );
  }, [proposedComplianceProgram]);
  const downwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.DownwardReview,
    );
  }, [proposedComplianceProgram]);
  const engagementSurveyStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.EngagementSurvey,
    );
  }, [proposedComplianceProgram]);

  const handleUpdateStepDueDateOffset = useCallback(
    (step: ComplianceProgramStep, offset: string | null) => {
      const existingStep = proposedComplianceProgram.steps.find(
        (s) => s.stepType === step,
      );
      if (existingStep) {
        onAddOrUpdateStep({
          ...existingStep,
          dueDateOffset: offset,
        } as typeof existingStep);
      }
    },
    [onAddOrUpdateStep, proposedComplianceProgram.steps],
  );

  const activeSteps = proposedComplianceProgram.steps.map(
    ({ stepType }) => stepType,
  );

  return (
    <>
      <div className="tf-header-base-dark">
        {complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
          ? "Survey Kick-off"
          : `${label("review", {
              capitalize: true,
            })} Kick-off`}
      </div>
      {complianceProgramType === ComplianceProgramType.REVIEW && (
        <div className="mt-1 text-gray-500">
          All items within a step will be available at the same time. Each step
          will be available once the previous step is completed.
        </div>
      )}

      <div className="mt-3 border rounded-md p-6">
        <FormFields
          fields={compact([
            {
              title:
                complianceProgramType ===
                ComplianceProgramType.ENGAGEMENT_SURVEY
                  ? "Survey name"
                  : `${label("review", { capitalize: true })} name`,
              id: "field-title",
              error: errorForField("title"),
              render: () => (
                <Input
                  aria-label={`${noun} title input`}
                  value={proposedComplianceProgram.title ?? ""}
                  placeholder={`Enter a ${complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY ? "survey" : label("review")} name...`}
                  onChange={(e) =>
                    onUpdateProposedComplianceProgram({
                      title: e.target.value,
                    })
                  }
                />
              ),
            },
            {
              title: "Cycle type",
              tooltip: `This will determine what conditions will make your ${nounLower} start.\n\nSingle cycle: This is a “one-off” ${nounLower} that will only run once and start and end on specific dates (with the option to have it repeat).\n\nConditional: Use this option when it should run continuously (e.g. a 90-day probationary ${nounLower}). New employees get added based on their employee start date.`,
              render: () => (
                <div className="w-fit">
                  <ToggleButtonGroup<ProgramStartTrigger>
                    theme={ToggleButtonGroupTheme.defaultButtons}
                    buttons={[
                      {
                        title: "Single Cycle",
                        active: !proposedComplianceProgram.ongoing,
                        value: ProgramStartTrigger.ON_START_DATE,
                      },
                      {
                        title: "Conditional",
                        active: !!proposedComplianceProgram.ongoing,
                        value: ProgramStartTrigger.EMPLOYEE_START_DATE,
                      },
                    ]}
                    onClick={(buttonGroup) => {
                      const isOngoing =
                        buttonGroup.value ===
                        ProgramStartTrigger.EMPLOYEE_START_DATE;
                      onUpdateProposedComplianceProgram({
                        startDate: undefined,
                        startDateOffset: undefined,
                        dueDate: undefined,
                        startDateDueDateOffset: undefined,
                        ongoing: isOngoing,
                        recurrence: isOngoing
                          ? ComplianceProgramRecurrence.None
                          : proposedComplianceProgram.recurrence,
                      });
                    }}
                  />
                </div>
              ),
            },
            complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY &&
              proposedComplianceProgram.ongoing && {
                title: "Evaluation period start",
                tooltip:
                  "This period of time will be used as a reference for the system to show relevant goals, work history, role details, etc.",
                id: "field-startDatePeriodStartOffset",
                error: errorForField("startDatePeriodStartOffset"),
                render: () => (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      className="w-24"
                      min={0}
                      step={1}
                      aria-label="Evaluation period start offset"
                      value={
                        proposedComplianceProgram.startDatePeriodStartOffset ??
                        ""
                      }
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          startDatePeriodStartOffset: Math.trunc(
                            Number(e.target.value),
                          ),
                        })
                      }
                    />
                    <div className="text-sm text-gray-600">
                      days after employee's start date
                    </div>
                  </div>
                ),
              },
            complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY &&
              proposedComplianceProgram.ongoing && {
                title: "Evaluation period end",
                id: "field-startDatePeriodEndOffset",
                error: errorForField("startDatePeriodEndOffset"),
                render: () => (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="number"
                      className="w-24"
                      min={0}
                      step={1}
                      aria-label="Evaluation period end offset"
                      value={
                        proposedComplianceProgram.startDatePeriodEndOffset ?? ""
                      }
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          startDatePeriodEndOffset: Math.trunc(
                            Number(e.target.value),
                          ),
                        })
                      }
                    />
                    <div className="text-sm text-gray-600">
                      days after employee's start date
                    </div>
                  </div>
                ),
              },
            !proposedComplianceProgram.ongoing && {
              title: "Start and due date",
              id: "field-startDate",
              error: errorForAnyField(["startDate", "dueDate"]),
              render: () => (
                <div className="flex gap-2 items-center">
                  <InputDatePicker
                    date={proposedComplianceProgram.startDate}
                    onChangeDate={(date) =>
                      onUpdateProposedComplianceProgram({
                        startDate: date
                          ? moment(date).format("YYYY-MM-DD")
                          : undefined,
                      })
                    }
                    wrapperClassName="flex-1"
                    placeholderText="Start date"
                    ariaLabelledBy="Start date"
                  />
                  <TopicflowIcon
                    type="rightArrow"
                    className="text-gray-500 w-4 h-4"
                  />
                  <InputDatePicker
                    date={proposedComplianceProgram.dueDate}
                    onChangeDate={(date) =>
                      onUpdateProposedComplianceProgram({
                        dueDate: date
                          ? moment(date).format("YYYY-MM-DD")
                          : undefined,
                      })
                    }
                    wrapperClassName="flex-1"
                    placeholderText="Due date"
                    ariaLabelledBy="Due date"
                  />
                </div>
              ),
            },
            proposedComplianceProgram.ongoing && {
              title: `${noun} kickoff date`,
              id: "field-startDateOffset",
              error: errorForField("startDateOffset"),
              render: () => (
                <div className="flex gap-2 items-center">
                  <Input
                    className="w-24"
                    type="number"
                    min={0}
                    step={1}
                    aria-label="Kickoff date offset"
                    value={proposedComplianceProgram.startDateOffset ?? ""}
                    onChange={(e) =>
                      onUpdateProposedComplianceProgram({
                        startDateOffset: Math.trunc(Number(e.target.value)),
                      })
                    }
                  />
                  <div className="text-sm text-gray-600">
                    days after employee's start date
                  </div>
                </div>
              ),
            },
            proposedComplianceProgram.ongoing && {
              title: `${noun} due date`,
              id: "field-startDateDueDateOffset",
              error: errorForField("startDateDueDateOffset"),
              render: () => (
                <div className="flex gap-2 items-center">
                  <Input
                    className="w-24"
                    type="number"
                    min={0}
                    step={1}
                    aria-label="Due date offset"
                    value={
                      isNumber(
                        proposedComplianceProgram.startDateDueDateOffset,
                      ) && isNumber(proposedComplianceProgram.startDateOffset)
                        ? proposedComplianceProgram.startDateDueDateOffset -
                          proposedComplianceProgram.startDateOffset
                        : isNumber(
                              proposedComplianceProgram.startDateDueDateOffset,
                            )
                          ? proposedComplianceProgram.startDateDueDateOffset
                          : ""
                    }
                    onChange={(e) =>
                      onUpdateProposedComplianceProgram({
                        startDateDueDateOffset: Math.trunc(
                          Number(e.target.value) +
                            (proposedComplianceProgram.startDateOffset ?? 0),
                        ),
                      })
                    }
                  />
                  <div className="text-sm text-gray-600">
                    days after kickoff
                  </div>
                </div>
              ),
            },
            !proposedComplianceProgram.ongoing && {
              title: `Repeat this ${
                complianceProgramType ===
                ComplianceProgramType.ENGAGEMENT_SURVEY
                  ? "survey"
                  : label("review")
              }`,
              render: () => (
                <Select<ComplianceProgramRecurrence>
                  value={
                    proposedComplianceProgram.recurrence ??
                    ComplianceProgramRecurrence.None
                  }
                  options={[
                    {
                      label: "Never",
                      value: ComplianceProgramRecurrence.None,
                    },
                    {
                      label: "Monthly",
                      value: ComplianceProgramRecurrence.Monthly,
                    },
                    {
                      label: "Quarterly",
                      value: ComplianceProgramRecurrence.Quarterly,
                    },
                    {
                      label: "Yearly",
                      value: ComplianceProgramRecurrence.Yearly,
                    },
                  ]}
                  onChange={(opt) =>
                    onUpdateProposedComplianceProgram({
                      recurrence: opt.value,
                    })
                  }
                />
              ),
            },
            complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY &&
              !proposedComplianceProgram.ongoing && {
                title: "Evaluation period",
                tooltip:
                  "This period of time will be used as a reference for the system to show relevant goals, work history, role details, etc.",
                id: "field-periodStartDate",
                error: errorForAnyField(["periodStartDate", "periodEndDate"]),
                render: () => (
                  <>
                    <Select<ProgramEvaluationPeriodType>
                      value={proposedComplianceProgram.evaluationPeriod.type}
                      options={[
                        {
                          label: "Past week",
                          value: ProgramEvaluationPeriodType.PAST_WEEK,
                        },
                        {
                          label: "Past 2 weeks",
                          value: ProgramEvaluationPeriodType.PAST_TWO_WEEKS,
                        },
                        {
                          label: "Past month",
                          value: ProgramEvaluationPeriodType.PAST_MONTH,
                        },
                        {
                          label: "Past 2 months",
                          value: ProgramEvaluationPeriodType.PAST_TWO_MONTHS,
                        },
                        {
                          label: "Past quarter",
                          value: ProgramEvaluationPeriodType.PAST_QUARTER,
                        },
                        {
                          label: "Past 6 months",
                          value: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
                        },
                        {
                          label: "Past year",
                          value: ProgramEvaluationPeriodType.PAST_YEAR,
                        },
                        {
                          label: "Custom date range",
                          value: ProgramEvaluationPeriodType.CUSTOM,
                        },
                      ]}
                      onChange={(opt) =>
                        onUpdateProposedComplianceProgram({
                          evaluationPeriod: {
                            type: opt.value,
                            startDate:
                              opt.value !== ProgramEvaluationPeriodType.CUSTOM
                                ? undefined
                                : proposedComplianceProgram.evaluationPeriod
                                    .startDate,
                            endDate:
                              opt.value !== ProgramEvaluationPeriodType.CUSTOM
                                ? undefined
                                : proposedComplianceProgram.evaluationPeriod
                                    .endDate,
                          },
                        })
                      }
                    />
                    {proposedComplianceProgram.evaluationPeriod.type ===
                      ProgramEvaluationPeriodType.CUSTOM && (
                      <div className="mt-2 flex gap-2 items-center">
                        <InputDatePicker
                          date={
                            proposedComplianceProgram.evaluationPeriod.startDate
                          }
                          placeholderText="Start date"
                          wrapperClassName="flex-1"
                          onChangeDate={(date) =>
                            onUpdateProposedComplianceProgram({
                              evaluationPeriod: {
                                ...proposedComplianceProgram.evaluationPeriod,
                                startDate: date
                                  ? moment(date).format("YYYY-MM-DD")
                                  : undefined,
                              },
                            })
                          }
                          ariaLabelledBy="Evaluation period start date"
                        />
                        <TopicflowIcon
                          type="rightArrow"
                          className="text-gray-500 w-4 h-4"
                        />
                        <InputDatePicker
                          date={
                            proposedComplianceProgram.evaluationPeriod.endDate
                          }
                          placeholderText="End date"
                          wrapperClassName="flex-1"
                          onChangeDate={(date) =>
                            onUpdateProposedComplianceProgram({
                              evaluationPeriod: {
                                ...proposedComplianceProgram.evaluationPeriod,
                                endDate: date
                                  ? moment(date).format("YYYY-MM-DD")
                                  : undefined,
                              },
                            })
                          }
                          ariaLabelledBy="Evaluation period end date"
                        />
                      </div>
                    )}
                  </>
                ),
              },
            complianceProgramType ===
              ComplianceProgramType.ENGAGEMENT_SURVEY && {
              title: "Anonymity",
              render: () =>
                engagementSurveyStep && (
                  <Select<AssessmentGroupAnonymity>
                    disabled={!canUpdate}
                    value={assertNonNull(engagementSurveyStep.anonymity)}
                    options={assessmentGroupAnonymityOptions(
                      AssessmentType.EngagementSurvey,
                    )}
                    onChange={(opt) =>
                      onAddOrUpdateStep({
                        ...engagementSurveyStep,
                        anonymity: opt.value,
                      })
                    }
                  />
                ),
            },
          ])}
        />
      </div>

      {complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY && (
        <>
          <div className="mt-12 tf-header-base-dark">{`Step 1: Employee ${label(
            "review",
            {
              capitalize: true,
              pluralize: true,
            },
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <ToggleCard
              title={`Pre-${label("review", { capitalize: true })} Calibration`}
              enabled={isPreCalibration}
              disabled={
                !activeSteps.includes(ComplianceProgramStep.DownwardReview) ||
                !canUpdate
              }
              infoTooltip={`You may only use one of the calibration types (pre/post-${label(
                "review",
              )}) in your ${label("review")}.`}
              toggleTooltip={
                activeSteps.includes(ComplianceProgramStep.DownwardReview)
                  ? undefined
                  : `Pre-${label("review", {
                      capitalize: true,
                    })} Calibration requires Downward ${label("review", {
                      capitalize: true,
                    })}`
              }
              onChange={(enabled) =>
                onUpdateProposedComplianceProgram({
                  calibration: enabled
                    ? ComplianceProgramCalibrationType.PreCalibration
                    : ComplianceProgramCalibrationType.Off,
                })
              }
              // Pre-calibration holds each manager review until the person is
              // marked as calibrated, which an admin can miss (TF-1679). A note,
              // not an error: validateProgramForm() errors block saving.
              settings={
                isPreCalibration && (
                  <Callout
                    icon="info"
                    size="small"
                    className="bg-blue-100 text-blue-900"
                  >
                    {`Managers cannot start a person's ${label(
                      "review",
                    )} until their pre-calibration is complete. Create calibration groups in the ${label(
                      "review",
                    )}'s Results tab${
                      isDraft ? " after you publish" : ""
                    }. A group approver or an HR admin then marks each person as calibrated.`}
                  </Callout>
                )
              }
            />
            <ToggleCard
              title={`Self ${label("review", { capitalize: true })}`}
              enabled={activeSteps.includes(ComplianceProgramStep.SelfReview)}
              infoTooltip={`The self ${label("review")} and downward ${label(
                "review",
              )} are currently linked. You cannot enable one without enabling the other.`}
              disabled={!canUpdate}
              onChange={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(ComplianceProgramStep.SelfReview),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.SelfReview);
                }
              }}
              settings={
                activeSteps.includes(ComplianceProgramStep.SelfReview) && (
                  <FormFields
                    fields={[
                      {
                        title: "Due Date",
                        render: () => (
                          <ProgramStepDueDate
                            ongoingProgram={!!proposedComplianceProgram.ongoing}
                            programStartDate={
                              proposedComplianceProgram.startDate
                            }
                            minStepDueDateOffset={getMinStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.SelfReview,
                            )}
                            maxStepDueDateOffset={getMaxStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.SelfReview,
                            )}
                            stepDueDateOffset={
                              proposedComplianceProgram.steps.find(
                                (s) =>
                                  s.stepType ===
                                  ComplianceProgramStep.SelfReview,
                              )?.dueDateOffset
                            }
                            onUpdate={(offset) =>
                              handleUpdateStepDueDateOffset(
                                ComplianceProgramStep.SelfReview,
                                offset,
                              )
                            }
                          />
                        ),
                      },
                    ]}
                  />
                )
              }
            />
            <ToggleCard
              title={`Peer ${label("review", { capitalize: true })}`}
              enabled={activeSteps.includes(ComplianceProgramStep.PeerReview)}
              disabled={!canUpdate}
              settings={
                activeSteps.includes(ComplianceProgramStep.PeerReview) && (
                  <FormFields
                    fields={[
                      {
                        title: "Due Date",
                        render: () => (
                          <ProgramStepDueDate
                            ongoingProgram={!!proposedComplianceProgram.ongoing}
                            programStartDate={
                              proposedComplianceProgram.startDate
                            }
                            minStepDueDateOffset={getMinStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.PeerReview,
                            )}
                            maxStepDueDateOffset={getMaxStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.PeerReview,
                            )}
                            stepDueDateOffset={
                              proposedComplianceProgram.steps.find(
                                (s) =>
                                  s.stepType ===
                                  ComplianceProgramStep.PeerReview,
                              )?.dueDateOffset
                            }
                            onUpdate={(offset) =>
                              handleUpdateStepDueDateOffset(
                                ComplianceProgramStep.PeerReview,
                                offset,
                              )
                            }
                          />
                        ),
                      },
                      {
                        title: "Providers",
                        render: () =>
                          peerReviewStep ? (
                            <DeprecatedSelect<AssessmentGroupProviders>
                              disabled={!canUpdate}
                              value={assertNonNull(peerReviewStep.providers)}
                              options={assessmentGroupProviderOptions(
                                AssessmentType.Peer,
                              )}
                              onChange={(opt) =>
                                onAddOrUpdateStep({
                                  ...peerReviewStep,
                                  providers: opt.value,
                                })
                              }
                              aria-label="Assessment providers select"
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                      {
                        title: "Anonymity",
                        render: () =>
                          peerReviewStep ? (
                            <DeprecatedSelect<AssessmentGroupAnonymity>
                              disabled={!canUpdate}
                              value={assertNonNull(peerReviewStep.anonymity)}
                              options={assessmentGroupAnonymityOptions(
                                AssessmentType.Peer,
                              )}
                              onChange={(opt) =>
                                onAddOrUpdateStep({
                                  ...peerReviewStep,
                                  anonymity: opt.value,
                                })
                              }
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                    ]}
                  />
                )
              }
              onChange={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(ComplianceProgramStep.PeerReview),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.PeerReview);
                }
              }}
            />
            <ToggleCard
              title={`Upward ${label("review", { capitalize: true })}`}
              enabled={activeSteps.includes(ComplianceProgramStep.UpwardReview)}
              disabled={!canUpdate}
              settings={
                activeSteps.includes(ComplianceProgramStep.UpwardReview) && (
                  <FormFields
                    fields={[
                      {
                        title: "Due Date",
                        render: () => (
                          <ProgramStepDueDate
                            ongoingProgram={!!proposedComplianceProgram.ongoing}
                            programStartDate={
                              proposedComplianceProgram.startDate
                            }
                            minStepDueDateOffset={getMinStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.UpwardReview,
                            )}
                            maxStepDueDateOffset={getMaxStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.UpwardReview,
                            )}
                            stepDueDateOffset={
                              proposedComplianceProgram.steps.find(
                                (s) =>
                                  s.stepType ===
                                  ComplianceProgramStep.UpwardReview,
                              )?.dueDateOffset
                            }
                            onUpdate={(offset) =>
                              handleUpdateStepDueDateOffset(
                                ComplianceProgramStep.UpwardReview,
                                offset,
                              )
                            }
                          />
                        ),
                      },
                      {
                        title: "Anonymity",
                        render: () =>
                          upwardReviewStep ? (
                            <DeprecatedSelect<AssessmentGroupAnonymity>
                              disabled={!canUpdate}
                              value={assertNonNull(upwardReviewStep.anonymity)}
                              options={assessmentGroupAnonymityOptions(
                                AssessmentType.Manager,
                              )}
                              onChange={(opt) =>
                                onAddOrUpdateStep({
                                  ...upwardReviewStep,
                                  anonymity: opt.value,
                                })
                              }
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                    ]}
                  />
                )
              }
              onChange={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(
                      ComplianceProgramStep.UpwardReview,
                    ),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.UpwardReview);
                }
              }}
            />
          </div>

          <div className="mt-12 tf-header-base-dark">{`Step 2: Manager ${label(
            "review",
            {
              capitalize: true,
            },
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <ToggleCard
              title={`Downward ${label("review", { capitalize: true })}`}
              enabled={activeSteps.includes(
                ComplianceProgramStep.DownwardReview,
              )}
              infoTooltip={`The self ${label("review")} and downward ${label(
                "review",
              )} are currently linked. You cannot enable one without enabling the other.`}
              disabled={!canUpdate}
              onChange={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(
                      ComplianceProgramStep.DownwardReview,
                    ),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.DownwardReview);
                }
              }}
              settings={
                activeSteps.includes(ComplianceProgramStep.DownwardReview) && (
                  <FormFields
                    fields={[
                      {
                        title: "Due Date",
                        render: () => (
                          <ProgramStepDueDate
                            ongoingProgram={!!proposedComplianceProgram.ongoing}
                            programStartDate={
                              proposedComplianceProgram.startDate
                            }
                            minStepDueDateOffset={getMinStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.DownwardReview,
                            )}
                            maxStepDueDateOffset={getMaxStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.DownwardReview,
                            )}
                            stepDueDateOffset={
                              proposedComplianceProgram.steps.find(
                                (s) =>
                                  s.stepType ===
                                  ComplianceProgramStep.DownwardReview,
                              )?.dueDateOffset
                            }
                            onUpdate={(offset) =>
                              handleUpdateStepDueDateOffset(
                                ComplianceProgramStep.DownwardReview,
                                offset,
                              )
                            }
                          />
                        ),
                      },
                    ]}
                  />
                )
              }
            />
          </div>

          <div className="mt-12 tf-header-base-dark">{`Step 3: Post-${label(
            "review",
            {
              capitalize: true,
            },
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <ToggleCard
              title={`Post-${label("review", {
                capitalize: true,
              })} Calibration`}
              disabled={
                !activeSteps.includes(ComplianceProgramStep.DownwardReview) ||
                !canUpdate
              }
              infoTooltip={`You may only use one of the calibration types (pre/post-${label(
                "review",
              )}) in your ${label("review")}.`}
              toggleTooltip={
                activeSteps.includes(ComplianceProgramStep.DownwardReview)
                  ? undefined
                  : `Post-${label("review", {
                      capitalize: true,
                    })} Calibration requires Downward ${label("review", {
                      capitalize: true,
                    })}`
              }
              enabled={
                proposedComplianceProgram.calibration ===
                ComplianceProgramCalibrationType.PostCalibration
              }
              onChange={(enabled) => {
                onUpdateProposedComplianceProgram({
                  calibration: enabled
                    ? ComplianceProgramCalibrationType.PostCalibration
                    : ComplianceProgramCalibrationType.Off,
                });
              }}
            />
            <ToggleCard
              title={`Share ${label("review", {
                capitalize: true,
              })} with Subject`}
              enabled={proposedComplianceProgram.sharesWithSubject}
              disabled={
                !canUpdate ||
                (!peerReviewStep && !downwardReviewStep && !upwardReviewStep)
              }
              infoTooltip={`This determines whether the final ${label(
                "review",
              )} will be shared with the subject.`}
              toggleTooltip={
                !peerReviewStep && !downwardReviewStep && !upwardReviewStep
                  ? `Sharing requires a ${label("review", {
                      capitalize: true,
                    })}`
                  : undefined
              }
              onChange={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(ComplianceProgramStep.Delivery),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.Delivery);
                }
                onUpdateProposedComplianceProgram({
                  sharesWithSubject: enabled,
                });
              }}
              settings={
                proposedComplianceProgram.sharesWithSubject && (
                  <FormFields
                    fields={[
                      {
                        title: "Due Date",
                        render: () => (
                          <ProgramStepDueDate
                            ongoingProgram={!!proposedComplianceProgram.ongoing}
                            programStartDate={
                              proposedComplianceProgram.startDate
                            }
                            minStepDueDateOffset={getMinStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.Delivery,
                            )}
                            maxStepDueDateOffset={getMaxStepDueDateOffset(
                              proposedComplianceProgram,
                              ComplianceProgramStep.Delivery,
                            )}
                            stepDueDateOffset={
                              proposedComplianceProgram.steps.find(
                                (s) =>
                                  s.stepType === ComplianceProgramStep.Delivery,
                              )?.dueDateOffset
                            }
                            onUpdate={(offset) =>
                              handleUpdateStepDueDateOffset(
                                ComplianceProgramStep.Delivery,
                                offset,
                              )
                            }
                          />
                        ),
                      },
                    ]}
                  />
                )
              }
            />
            <OneOnOneToggleCard
              canUpdate={canUpdate}
              proposedComplianceProgram={proposedComplianceProgram}
              onAddOrUpdateStep={onAddOrUpdateStep}
              errors={errors}
              onToggleStep={(enabled) => {
                if (enabled) {
                  onAddOrUpdateStep(
                    getDefaultProgramFormStep(ComplianceProgramStep.OneOnOne),
                  );
                } else {
                  onRemoveStep(ComplianceProgramStep.OneOnOne);
                }
              }}
              onUpdateStepDueDateOffset={(offset) =>
                handleUpdateStepDueDateOffset(
                  ComplianceProgramStep.OneOnOne,
                  offset,
                )
              }
            />
          </div>
        </>
      )}
    </>
  );
};

export default ProgramFormWorkflow;
